import type {
  DashboardResolveWidgetLine,
  DashboardTextWidget,
  ResolvedDashboardWidget,
} from "@t3tools/contracts";
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  type ChartEvent,
} from "chart.js";
import { CircleAlertIcon, DownloadIcon, ImageDownIcon } from "lucide-react";
import { useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Chart } from "react-chartjs-2";

import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/utils";

import { ChartPanel, DataTable, KpiCard, type DataTableColumn } from "../dashboardKit";
import { exportChartToPng, exportRowsToCsv } from "../dashboardsExport";
import {
  ArtifactChartPointPopover,
  ArtifactTrustButton,
  ArtifactTrustPopover,
  ArtifactWidgetFreshness,
  type ArtifactTrustDetailsProps,
  type ArtifactTrustPoint,
} from "./ArtifactTrustPopover";
import {
  buildArtifactChart,
  type ArtifactChartType,
  type ArtifactChartWidget,
} from "./artifactChart";
import {
  EMPTY_VALUE,
  artifactTableColumnLabel,
  artifactCsvRows,
  artifactExportFilename,
  artifactKpiDelta,
  artifactWidgetColumns,
  artifactWidgetDefinitions,
  fillArtifactFootnote,
  formatArtifactNumber,
  formatArtifactValue,
} from "./artifactFormat";
import { LOADING_WIDGET, type ArtifactWidgetState } from "./artifactResolve";
import { artifactTrust, isSettlingPoint, type ArtifactTrust } from "./artifactTrust";
import type {
  ArtifactColumn,
  ArtifactDataWidget,
  ArtifactRow,
  ArtifactSpec,
  ArtifactWidget as ArtifactWidgetSpec,
  ResolvedWidgetOf,
} from "./artifactTypes";

ChartJS.register(
  BarController,
  BarElement,
  CategoryScale,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
);

/** Everything a data widget needs once its line has arrived. */
type Loaded<K extends ArtifactDataWidget["kind"]> = {
  line: DashboardResolveWidgetLine;
  resolved: ResolvedWidgetOf<K>;
  trust: ArtifactTrust;
};

type WidgetContext = {
  artifactTitle: string;
  columns: ReadonlyMap<string, ArtifactColumn>;
  /** Trust details without the per-number parts. */
  details: Omit<ArtifactTrustDetailsProps, "line" | "point" | "trust" | "value">;
  state: ArtifactWidgetState;
};

function WidgetError(props: { message: string }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-2 rounded-lg border border-destructive/32 bg-destructive/4 p-3 text-sm"
    >
      <CircleAlertIcon className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
      <div>
        <p className="font-medium text-foreground">This widget could not load.</p>
        <p className="text-muted-foreground">{props.message}</p>
      </div>
    </div>
  );
}

/** The line's data when it matches the widget's kind, or why it cannot draw. */
function useLoaded<K extends ArtifactDataWidget["kind"]>(
  state: ArtifactWidgetState,
  kind: K,
  now: Date,
): { error: string | null; loaded: Loaded<K> | null } {
  const line = state.status === "ready" ? state.line : null;
  const trust = useMemo(() => (line ? artifactTrust(line, now) : null), [line, now]);
  if (state.status === "error") return { error: state.message, loaded: null };
  if (!line || !trust) return { error: null, loaded: null };
  const resolved: ResolvedDashboardWidget = line.resolved;
  if (resolved.kind !== kind) {
    return {
      error: `Expected ${kind} data but the server returned ${resolved.kind}`,
      loaded: null,
    };
  }
  return { error: null, loaded: { line, resolved: resolved as ResolvedWidgetOf<K>, trust } };
}

function WidgetFooter(props: { trust: ArtifactTrust | null }) {
  return props.trust ? (
    <p className="mt-3 text-xs">
      <ArtifactWidgetFreshness trust={props.trust} />
    </p>
  ) : null;
}

function WidgetDescription(props: { text: string | undefined }) {
  return props.text ? (
    <p className="-mt-2 mb-3 text-xs text-muted-foreground">{props.text}</p>
  ) : null;
}

function WidgetActions(props: {
  children?: ReactNode;
  context: WidgetContext;
  loaded: Loaded<"chart"> | Loaded<"table"> | null;
  widget: Extract<ArtifactDataWidget, { kind: "chart" | "table" }>;
}) {
  const { context, loaded, widget } = props;
  const rows = loaded ? artifactCsvRows(widget, loaded.resolved, context.columns) : [];
  return (
    <div className="flex flex-wrap items-center gap-1">
      <div className="flex items-center gap-1" data-dashboard-pdf-export="">
        {props.children}
        {rows.length ? (
          <Button
            size="xs"
            variant="ghost"
            aria-label={`Export ${widget.title} as CSV`}
            onClick={() =>
              void exportRowsToCsv(
                rows,
                artifactExportFilename(context.artifactTitle, widget.id, "csv"),
              )
            }
          >
            <DownloadIcon aria-hidden />
            CSV
          </Button>
        ) : null}
      </div>
      {loaded ? (
        <ArtifactTrustButton
          {...context.details}
          line={loaded.line}
          point={null}
          trust={loaded.trust}
        />
      ) : null}
    </div>
  );
}

function WidgetBody(props: {
  children: () => ReactNode;
  empty: boolean;
  emptyMessage: string;
  error: string | null;
  loading: ReactNode;
  state: ArtifactWidgetState;
}) {
  const { state } = props;
  if (props.error) return <WidgetError message={props.error} />;
  if (state.status === "loading") return <>{props.loading}</>;
  if (props.empty) {
    return <p className="py-6 text-center text-sm text-muted-foreground">{props.emptyMessage}</p>;
  }
  return (
    <div
      className={cn(
        state.status === "ready" && state.refreshing && "opacity-70 transition-opacity",
      )}
    >
      {props.children()}
    </div>
  );
}

function KpiWidget(props: {
  context: WidgetContext;
  now: Date;
  widget: Extract<ArtifactDataWidget, { kind: "kpi" }>;
}) {
  const { context, widget } = props;
  const { state } = context;
  const { error, loaded } = useLoaded(state, "kpi", props.now);
  const resolved = loaded?.resolved;
  const hasRow = resolved?.hasRow === true;
  const suppressed = resolved?.suppressed === true;
  const delta =
    resolved && !suppressed ? artifactKpiDelta(widget, resolved, context.columns) : null;
  const value =
    resolved && hasRow && !suppressed
      ? formatArtifactValue(resolved.value.value, context.columns.get(widget.value))
      : null;
  const note = error ? (
    <span role="alert" className="block text-destructive">
      Could not load: {error}
    </span>
  ) : resolved && !hasRow ? (
    <span className="block">No data for these filters.</span>
  ) : suppressed ? (
    <span className="block">Hidden: a count under 11 could identify members.</span>
  ) : widget.footnote && resolved ? (
    <span className="block">
      {fillArtifactFootnote(widget.footnote, resolved, context.columns)}
    </span>
  ) : null;

  return (
    <div
      className={cn(
        "h-full [&>article]:h-full",
        state.status === "ready" && state.refreshing && "opacity-70 transition-opacity",
      )}
      data-artifact-state={state.status}
    >
      <KpiCard
        label={widget.title}
        value={
          state.status === "loading" ? (
            <Skeleton className="h-8 w-24" aria-label={`Loading ${widget.title}`} />
          ) : loaded && value !== null ? (
            <ArtifactTrustPopover
              {...context.details}
              label={`${widget.title}: ${value}`}
              line={loaded.line}
              point={null}
              trust={loaded.trust}
              value={value}
            >
              {value}
            </ArtifactTrustPopover>
          ) : (
            EMPTY_VALUE
          )
        }
        delta={delta?.text}
        deltaDirection={delta?.direction}
        footnote={
          note || loaded ? (
            <>
              {note}
              {loaded ? <ArtifactWidgetFreshness trust={loaded.trust} /> : null}
            </>
          ) : null
        }
      />
    </div>
  );
}

type ActivePoint = { series: number; index: number; x: number; y: number };

function ChartWidget(props: { context: WidgetContext; now: Date; widget: ArtifactChartWidget }) {
  const { context, widget } = props;
  const chartRef = useRef<ChartJS<ArtifactChartType> | null>(null);
  const frameRef = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState<ActivePoint | null>(null);
  const { error, loaded } = useLoaded(context.state, "chart", props.now);
  const resolved = loaded?.resolved ?? null;
  const settling = loaded?.trust.settling ?? null;
  const settlingFrom = useMemo(() => {
    if (!resolved || !settling) return undefined;
    const index = resolved.x.values.findIndex((x) => isSettlingPoint(settling, x));
    return index === -1 ? undefined : index;
  }, [resolved, settling]);
  const config = useMemo(() => {
    if (!resolved) return null;
    const built = buildArtifactChart(widget, resolved, context.columns, { settlingFrom });
    return {
      ...built,
      options: {
        ...built.options,
        onClick: (event: ChartEvent, _elements: unknown, chart: ChartJS) => {
          const native = event.native;
          if (!native) return;
          const [nearest] = chart.getElementsAtEventForMode(
            native,
            "nearest",
            { intersect: false },
            true,
          );
          if (!nearest) return;
          setActive({
            series: nearest.datasetIndex,
            index: nearest.index,
            x: nearest.element.x,
            y: nearest.element.y,
          });
        },
      },
    };
  }, [widget, resolved, context.columns, settlingFrom]);
  const empty = !resolved?.x.values.length;
  const height =
    widget.orientation === "horizontal"
      ? Math.max(220, (config?.data.labels?.length ?? 0) * 28)
      : 260;

  const point = useMemo((): ArtifactTrustPoint | null => {
    if (!active || !resolved || !config) return null;
    const series = resolved.series[active.series];
    if (!series) return null;
    const raw = series.values[active.index];
    const axisFormat = context.columns.get(series.column)?.format;
    return {
      x: String(config.data.labels?.[active.index] ?? ""),
      series: String(config.data.datasets[active.series]?.label ?? ""),
      value:
        raw === null || raw === undefined ? EMPTY_VALUE : formatArtifactNumber(raw, axisFormat),
      settling: isSettlingPoint(settling, resolved.x.values[active.index] ?? null),
      suppressed: series.suppressed?.includes(active.index) === true,
    };
  }, [active, resolved, config, context.columns, settling]);

  /** Moves the open popover between points: left/right by x, up/down by series. */
  const moveTo = (series: number, index: number) => {
    const chart = chartRef.current;
    if (!chart || !resolved) return;
    const seriesCount = resolved.series.length;
    const xCount = resolved.x.values.length;
    if (!seriesCount || !xCount) return;
    const nextSeries = (series + seriesCount) % seriesCount;
    const nextIndex = Math.max(0, Math.min(xCount - 1, index));
    const element = chart.getDatasetMeta(nextSeries).data[nextIndex];
    if (!element) return;
    setActive({ series: nextSeries, index: nextIndex, x: element.x, y: element.y });
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const current = active ?? { series: 0, index: (resolved?.x.values.length ?? 1) - 1 };
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [current.series, current.index - 1],
      ArrowRight: [current.series, current.index + 1],
      ArrowUp: [current.series - 1, current.index],
      ArrowDown: [current.series + 1, current.index],
      Home: [current.series, 0],
      End: [current.series, Number.MAX_SAFE_INTEGER],
    };
    const move = moves[event.key];
    if (move) {
      event.preventDefault();
      moveTo(move[0], move[1]);
    } else if (event.key === "Escape") {
      setActive(null);
    }
  };

  return (
    <ChartPanel
      title={widget.title}
      action={
        <WidgetActions context={context} loaded={loaded} widget={widget}>
          {!empty ? (
            <Button
              size="xs"
              variant="ghost"
              aria-label={`Export ${widget.title} as PNG`}
              onClick={() =>
                void exportChartToPng(
                  chartRef.current?.canvas,
                  artifactExportFilename(context.artifactTitle, widget.id, "png"),
                )
              }
            >
              <ImageDownIcon aria-hidden />
              PNG
            </Button>
          ) : null}
        </WidgetActions>
      }
    >
      <WidgetDescription text={widget.description} />
      <WidgetBody
        state={context.state}
        empty={empty}
        emptyMessage="No data for these filters."
        error={error}
        loading={
          <Skeleton className="w-full" style={{ height }} aria-label={`Loading ${widget.title}`} />
        }
      >
        {() =>
          config && loaded ? (
            <div
              ref={frameRef}
              className="relative rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring"
              style={{ height }}
              tabIndex={0}
              role="group"
              aria-label={`${widget.title}. Use the arrow keys to read each point.`}
              data-artifact-chart={widget.id}
              onKeyDown={onKeyDown}
              onFocus={(event) => {
                if (
                  event.target === event.currentTarget &&
                  event.currentTarget.matches(":focus-visible")
                ) {
                  moveTo(0, (resolved?.x.values.length ?? 1) - 1);
                }
              }}
            >
              <Chart
                ref={chartRef}
                type={config.type}
                data={config.data}
                options={config.options}
                aria-label={widget.title}
                role="img"
              />
              <ArtifactChartPointPopover
                {...context.details}
                anchor={
                  active && frameRef.current
                    ? { x: active.x, y: active.y, element: frameRef.current }
                    : null
                }
                line={loaded.line}
                point={point}
                trust={loaded.trust}
                onClose={() => setActive(null)}
              />
            </div>
          ) : null
        }
      </WidgetBody>
      <WidgetFooter trust={loaded?.trust ?? null} />
    </ChartPanel>
  );
}

function TableWidget(props: {
  context: WidgetContext;
  now: Date;
  widget: Extract<ArtifactDataWidget, { kind: "table" }>;
}) {
  const { context, widget } = props;
  const { error, loaded } = useLoaded(context.state, "table", props.now);
  const resolved = loaded?.resolved ?? null;
  const rows = useMemo(
    () =>
      resolved?.rows.map(
        (cells): ArtifactRow =>
          Object.fromEntries(resolved.columns.map((column, index) => [column, cells[index]!])),
      ) ?? [],
    [resolved],
  );
  const definitions = new Map(context.details.definitions.map((item) => [item.id, item.text]));
  const columns: Array<DataTableColumn<ArtifactRow>> = widget.columns.map((column) => {
    const meta = context.columns.get(column.column);
    const numeric = meta?.type === "number";
    return {
      key: column.column,
      label: artifactTableColumnLabel(column, resolved, context.columns),
      align: column.align ?? (numeric ? "right" : "left"),
      ...(column.definition && definitions.has(column.definition)
        ? { definition: definitions.get(column.definition)! }
        : {}),
      sortable: column.sortable === true || widget.defaultSort?.column === column.column,
      sortValue: (row) => {
        const value = row[column.column];
        return typeof value === "boolean" ? String(value) : value;
      },
      render: (row) => formatArtifactValue(row[column.column], meta),
    };
  });

  return (
    <ChartPanel
      title={widget.title}
      action={<WidgetActions context={context} loaded={loaded} widget={widget} />}
    >
      <WidgetDescription text={widget.description} />
      <WidgetBody
        state={context.state}
        empty={rows.length === 0}
        emptyMessage={widget.emptyMessage ?? "No rows for these filters."}
        error={error}
        loading={
          <div className="space-y-2" aria-label={`Loading ${widget.title}`}>
            {[0, 1, 2, 3].map((index) => (
              <Skeleton key={index} className="h-7 w-full" />
            ))}
          </div>
        }
      >
        {() => (
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(_row, index) => String(index)}
            {...(widget.defaultSort
              ? {
                  defaultSort: {
                    key: widget.defaultSort.column,
                    direction: widget.defaultSort.direction,
                  },
                }
              : {})}
            maxHeightClass="max-h-[28rem]"
          />
        )}
      </WidgetBody>
      <WidgetFooter trust={loaded?.trust ?? null} />
    </ChartPanel>
  );
}

function TextWidget(props: { widget: DashboardTextWidget }) {
  const paragraphs = props.widget.body
    .split(/\n\s*\n/)
    .map((text) => text.trim())
    .filter(Boolean);
  return (
    <ChartPanel {...(props.widget.title ? { title: props.widget.title } : {})}>
      <div className="space-y-2 text-sm leading-relaxed text-muted-foreground">
        {paragraphs.map((text) => (
          <p key={text} className="whitespace-pre-line">
            {text}
          </p>
        ))}
      </div>
    </ChartPanel>
  );
}

/**
 * One spec widget on the kit, with its own skeleton, error and empty states.
 * Metric and ad-hoc widgets draw the same way; only their trust differs.
 */
export function ArtifactWidget(props: {
  artifactTitle: string;
  /** Pins "Updated X min ago" and staleness. */
  now: Date;
  spec: ArtifactSpec;
  state: ArtifactWidgetState | undefined;
  widget: ArtifactWidgetSpec;
}) {
  const { spec, widget } = props;
  const state = props.state ?? LOADING_WIDGET;
  const provenance =
    state.status === "ready"
      ? state.line.provenance
      : state.status === "error"
        ? state.provenance
        : undefined;
  const columns = useMemo(
    () => artifactWidgetColumns(spec, widget, provenance),
    [spec, widget, provenance],
  );
  if (widget.kind === "text") return <TextWidget widget={widget} />;
  const dataSource = widget.data;
  const context: WidgetContext = {
    artifactTitle: props.artifactTitle,
    columns,
    details: {
      definitions: artifactWidgetDefinitions(spec, widget),
      dataset:
        dataSource.kind === "dataset"
          ? spec.datasets.find((dataset) => dataset.id === dataSource.dataset)
          : undefined,
      title: widget.title,
      widgetId: widget.id,
    },
    state,
  };
  if (widget.kind === "kpi") return <KpiWidget context={context} now={props.now} widget={widget} />;
  if (widget.kind === "chart")
    return <ChartWidget context={context} now={props.now} widget={widget} />;
  return <TableWidget context={context} now={props.now} widget={widget} />;
}
